import React, { useEffect, useState } from "react";
import { apiFetch, apiFetchDownload } from "../../api/apiFetch";
import { formatNumber, formatDate, formatDateTime } from "../../utils/format";
import ErrorModal from "../../components/ErrorModal";
import ConfirmModal from "../../components/ConfirmModal";
import { usePermission } from "../../hooks/usePermission";

const HUUR_PCT = 0.15;
const MAANDEN = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];

// Werkflow-status van een opgeslagen ingave. "nieuw" -> "bonnetjes_afgeleverd" gebeurt automatisch
// bij het genereren van een PDF-extract; de overige overgangen wijzig je manueel (bv. omdat het
// genereren van een SEPA-bestand niet garandeert dat de betaling effectief uitgevoerd is).
const STATUSES = [
  { value: "nieuw", label: "Nieuw", chip: "bg-gray-100 text-gray-700" },
  { value: "bonnetjes_afgeleverd", label: "Bonnetjes afgeleverd", chip: "bg-amber-100 text-amber-800" },
  { value: "bonnetjes_ontvangen", label: "Bonnetjes ontvangen", chip: "bg-blue-100 text-blue-800" },
  { value: "betaald", label: "Betaald", chip: "bg-green-100 text-green-800" },
];
const STATUS_CHIP = Object.fromEntries(STATUSES.map((s) => [s.value, s.chip]));

// Lokale (niet-UTC) datum -> "YYYY-MM-DD". toISOString() converteert naar UTC, wat in de
// Belgische tijdzone (UTC+1/+2) een middernacht-datum een dag terug laat verschuiven.
function toLocalISO(y, m, d) {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function todayISO() {
  const d = new Date();
  return toLocalISO(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

function firstOfQuarterISO() {
  const d = new Date();
  const quarterStartMonth = Math.floor(d.getMonth() / 3) * 3; // 0, 3, 6 of 9
  return toLocalISO(d.getFullYear(), quarterStartMonth + 1, 1);
}

const monthKey = (werkdag) => werkdag.slice(0, 7); // "2026-07"
const monthLabel = (key) => {
  const [jaar, maand] = key.split("-");
  return `${MAANDEN[parseInt(maand, 10) - 1]} ${jaar}`;
};

const quarterKey = (werkdag) => {
  const [jaar, maand] = werkdag.split("-");
  const kwartaal = Math.ceil(parseInt(maand, 10) / 3);
  return `${jaar}-Q${kwartaal}`;
};
const quarterLabel = (key) => key.replace("-", " ");

// Het ingegeven bedrag is steeds incl. BTW; het te storten bedrag (85%) wordt altijd berekend
// op het bedrag exclusief BTW, dus het BTW-deel moet er eerst uit gehaald worden.
const exclBtw = (bedrag, btwPct) => bedrag / (1 + btwPct);
const sumExclBtw = (entries) =>
  entries.reduce((s, e) => s + exclBtw(parseFloat(e.bedrag), parseFloat(e.btw_pct)), 0);
const teStortenVoor = (entries) => sumExclBtw(entries) * (1 - HUUR_PCT);

function groupByPeriod(entries, keyFn) {
  const map = {};
  entries.forEach((e) => {
    const key = keyFn(e.werkdag);
    if (!map[key]) map[key] = { patienten: 0, omzet: 0, omzetExclBtw: 0 };
    map[key].patienten += 1;
    map[key].omzet += parseFloat(e.bedrag);
    map[key].omzetExclBtw += exclBtw(parseFloat(e.bedrag), parseFloat(e.btw_pct));
  });
  return Object.entries(map)
    .map(([key, v]) => ({
      key,
      patienten: v.patienten,
      omzet: v.omzet,
      marge: v.omzetExclBtw * HUUR_PCT,
      betaaldAanSaskia: v.omzetExclBtw * (1 - HUUR_PCT),
    }))
    .sort((a, b) => a.key.localeCompare(b.key));
}

const HISTORY_PAGE_SIZE = 5;

function paginate(items, page) {
  const totalPages = Math.max(1, Math.ceil(items.length / HISTORY_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = items.slice((currentPage - 1) * HISTORY_PAGE_SIZE, currentPage * HISTORY_PAGE_SIZE);
  return { pageItems, totalPages, currentPage };
}

const OVERVIEW_SCOPES = [
  { value: "jaar", label: "Huidig jaar" },
  { value: "kwartaal", label: "Huidig kwartaal" },
  { value: "alles", label: "Alles" },
];

function filterByScope(entries, scope) {
  if (scope === "alles") return entries;
  const now = new Date();
  const currentYear = now.getFullYear();
  if (scope === "jaar") {
    return entries.filter((e) => parseInt(e.werkdag.slice(0, 4), 10) === currentYear);
  }
  const currentQuarter = Math.ceil((now.getMonth() + 1) / 3);
  return entries.filter((e) => {
    const [jaar, maand] = e.werkdag.split("-");
    return parseInt(jaar, 10) === currentYear && Math.ceil(parseInt(maand, 10) / 3) === currentQuarter;
  });
}

export default function BoekjeSaskia({ user }) {
  const { hasPermission } = usePermission();
  const canManage = hasPermission("boekje_saskia.manage");

  const [van, setVan] = useState(firstOfQuarterISO());
  const [tot, setTot] = useState(todayISO());
  const [status, setStatus] = useState("alle");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });

  const [exports, setExports] = useState([]);
  const [payments, setPayments] = useState([]);
  const [allEntries, setAllEntries] = useState([]); // ongefilterd, voor het maand-/kwartaaloverzicht

  const [form, setForm] = useState({
    werkdag: todayISO(),
    bedrag: "45.00",
    btw_pct: "0",
    betaalwijze: "Bancontact",
    notitie: "",
  });

  // Lokaal gestagede patiëntlijnen: verzameld via het plusje, pas naar de backend
  // gestuurd bij "Doorvoeren" — zo hoef je niet te wachten op elke individuele save.
  const [pendingEntries, setPendingEntries] = useState([]);
  const [savingBatch, setSavingBatch] = useState(false);

  const [selectedDays, setSelectedDays] = useState(new Set());
  const [busyAction, setBusyAction] = useState(null); // null | "export" | "sepa"
  const [deleteTarget, setDeleteTarget] = useState(null); // entry die verwijderd wordt na bevestiging
  const [deleting, setDeleting] = useState(false);
  const [deleteExportTarget, setDeleteExportTarget] = useState(null);
  const [deletingExport, setDeletingExport] = useState(false);
  const [deletePaymentTarget, setDeletePaymentTarget] = useState(null);
  const [deletingPayment, setDeletingPayment] = useState(false);
  const [expandedDays, setExpandedDays] = useState(new Set()); // standaard ingeklapt
  const [overviewScope, setOverviewScope] = useState("jaar");
  const [exportsPage, setExportsPage] = useState(1);
  const [paymentsPage, setPaymentsPage] = useState(1);

  useEffect(() => {
    fetchEntries();
  }, [van, tot, status]);

  useEffect(() => {
    fetchHistory();
    fetchAllEntries();
  }, []);

  const fetchEntries = () => {
    setLoading(true);
    apiFetch(`/income/boekje-saskia/entries?van=${van}&tot=${tot}&status=${status}`)
      .then(setEntries)
      .catch((err) =>
        setErrorModal({ show: true, message: err.message || "Fout bij ophalen van ingaves." })
      )
      .finally(() => setLoading(false));
  };

  const fetchAllEntries = () => {
    apiFetch("/income/boekje-saskia/entries?status=alle").then(setAllEntries).catch(() => {});
  };

  const fetchHistory = () => {
    apiFetch("/income/boekje-saskia/exports").then(setExports).catch(() => {});
    apiFetch("/income/boekje-saskia/payments").then(setPayments).catch(() => {});
  };

  const handleAdd = (e) => {
    e.preventDefault();
    if (!form.werkdag || !form.bedrag) return;

    setPendingEntries((prev) => [
      ...prev,
      {
        _tempId: `pending-${prev.length}-${Date.now()}`,
        _pending: true,
        werkdag: form.werkdag,
        bedrag: parseFloat(form.bedrag),
        btw_pct: parseFloat(form.btw_pct),
        betaalwijze: form.betaalwijze,
        notitie: form.notitie || null,
      },
    ]);
    // Datum/bedrag/BTW/betaalwijze blijven staan zodat de volgende patiënt met
    // hetzelfde plusje toegevoegd kan worden; enkel de notitie is per patiënt uniek.
    setForm({ ...form, notitie: "" });
  };

  const handleBedragBlur = () => {
    const parsed = parseFloat(form.bedrag);
    setForm({ ...form, bedrag: isNaN(parsed) ? "0.00" : parsed.toFixed(2) });
  };

  const removePending = (tempId) => {
    setPendingEntries((prev) => prev.filter((p) => p._tempId !== tempId));
  };

  const toggleDay = (day) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  };

  const toggleDaySelection = (day) => {
    setSelectedDays((prev) => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  };

  const handleSaveBatch = async () => {
    if (pendingEntries.length === 0) return;
    setSavingBatch(true);
    try {
      await apiFetch("/income/boekje-saskia/entries/batch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entries: pendingEntries.map(({ werkdag, bedrag, btw_pct, betaalwijze, notitie }) => ({
            werkdag,
            bedrag,
            btw_pct,
            betaalwijze,
            notitie,
          })),
        }),
      });

      // Verruim de periodefilter indien nodig, zodat net opgeslagen lijnen buiten de huidige
      // filter niet plots "verdwijnen" uit de tabel. Bij een verruiming triggert de
      // [van, tot, status]-effect zelf een verse fetchEntries(); anders doen we dat hier.
      const savedDates = pendingEntries.map((p) => p.werkdag).sort();
      const newVan = savedDates[0] < van ? savedDates[0] : van;
      const newTot = savedDates[savedDates.length - 1] > tot ? savedDates[savedDates.length - 1] : tot;
      const rangeChanged = newVan !== van || newTot !== tot;

      setPendingEntries([]);
      if (rangeChanged) {
        setVan(newVan);
        setTot(newTot);
      } else {
        fetchEntries();
      }
      fetchAllEntries();
    } catch (err) {
      setErrorModal({ show: true, message: err.message || "Fout bij opslaan." });
    } finally {
      setSavingBatch(false);
    }
  };

  const handleDayStatusChange = async (day, newStatus) => {
    if (!newStatus) return;
    try {
      await apiFetch("/income/boekje-saskia/entries/status/bulk", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ werkdag: day, status: newStatus }),
      });
      fetchEntries();
      fetchAllEntries();
    } catch (err) {
      setErrorModal({ show: true, message: err.message || "Fout bij wijzigen van status." });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiFetch(`/income/boekje-saskia/entries/${deleteTarget.id}`, { method: "DELETE" });
      setDeleteTarget(null);
      fetchEntries();
      fetchAllEntries();
    } catch (err) {
      setErrorModal({ show: true, message: err.message || "Fout bij verwijderen." });
    } finally {
      setDeleting(false);
    }
  };

  const handleExportSelected = async () => {
    setBusyAction("export");
    try {
      const params = [...selectedDays].map((d) => `dagen=${d}`).join("&");
      await apiFetchDownload(`/income/boekje-saskia/export?${params}`);
      setSelectedDays(new Set());
      fetchEntries();
      fetchAllEntries();
      fetchHistory();
    } catch (err) {
      setErrorModal({ show: true, message: err.message || "Fout bij genereren van extract." });
    } finally {
      setBusyAction(null);
    }
  };

  const handleSepaSelected = async () => {
    setBusyAction("sepa");
    try {
      await apiFetchDownload("/income/boekje-saskia/sepa", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ werkdagen: [...selectedDays] }),
      });
      setSelectedDays(new Set());
      fetchEntries();
      fetchAllEntries();
      fetchHistory();
    } catch (err) {
      setErrorModal({ show: true, message: err.message || "Fout bij genereren van SEPA-bestand." });
    } finally {
      setBusyAction(null);
    }
  };

  const downloadExport = (id) =>
    apiFetchDownload(`/income/boekje-saskia/exports/${id}/download`).catch((err) =>
      setErrorModal({ show: true, message: err.message })
    );

  const confirmDeleteExport = async () => {
    if (!deleteExportTarget) return;
    setDeletingExport(true);
    try {
      await apiFetch(`/income/boekje-saskia/exports/${deleteExportTarget.id}`, { method: "DELETE" });
      setDeleteExportTarget(null);
      fetchHistory();
    } catch (err) {
      setErrorModal({ show: true, message: err.message || "Fout bij verwijderen van extract." });
    } finally {
      setDeletingExport(false);
    }
  };

  const downloadPayment = (id) =>
    apiFetchDownload(`/income/boekje-saskia/payments/${id}/download`).catch((err) =>
      setErrorModal({ show: true, message: err.message })
    );

  const confirmDeletePayment = async () => {
    if (!deletePaymentTarget) return;
    setDeletingPayment(true);
    try {
      await apiFetch(`/income/boekje-saskia/payments/${deletePaymentTarget.id}`, { method: "DELETE" });
      setDeletePaymentTarget(null);
      fetchHistory();
      fetchEntries();
      fetchAllEntries();
    } catch (err) {
      setErrorModal({ show: true, message: err.message || "Fout bij verwijderen van betaling." });
    } finally {
      setDeletingPayment(false);
    }
  };

  const renderHistoryPagination = (currentPage, totalPages, setPage) =>
    totalPages > 1 && (
      <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-300 disabled:cursor-not-allowed"
        >
          ← Vorige
        </button>
        <span className="text-xs text-gray-400">
          Pagina {currentPage} van {totalPages}
        </span>
        <button
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-300 disabled:cursor-not-allowed"
        >
          Volgende →
        </button>
      </div>
    );

  const renderPeriodTable = (title, rows, labelFn) => {
    const totals = rows.reduce(
      (acc, r) => ({
        patienten: acc.patienten + r.patienten,
        omzet: acc.omzet + r.omzet,
        marge: acc.marge + r.marge,
        betaaldAanSaskia: acc.betaaldAanSaskia + r.betaaldAanSaskia,
      }),
      { patienten: 0, omzet: 0, marge: 0, betaaldAanSaskia: 0 }
    );

    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <h3 className="text-sm font-semibold text-gray-700 px-4 py-3 border-b border-gray-100">{title}</h3>
        {rows.length === 0 ? (
          <p className="text-xs text-gray-400 px-4 py-4">Nog geen gegevens.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Periode</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Patiënten</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Omzet Saskia</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Onze marge (15%)</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Betaald aan Saskia (85%)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-700">{labelFn(r.key)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-700">{r.patienten}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium text-gray-800">
                    € {formatNumber(r.omzet)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-600">€ {formatNumber(r.marge)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-600">
                    € {formatNumber(r.betaaldAanSaskia)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 border-t border-gray-200 font-semibold text-gray-700">
                <td className="px-4 py-2">Totaal</td>
                <td className="px-4 py-2 text-right tabular-nums">{totals.patienten}</td>
                <td className="px-4 py-2 text-right tabular-nums">€ {formatNumber(totals.omzet)}</td>
                <td className="px-4 py-2 text-right tabular-nums">€ {formatNumber(totals.marge)}</td>
                <td className="px-4 py-2 text-right tabular-nums">€ {formatNumber(totals.betaaldAanSaskia)}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    );
  };

  // Groepeer opgeslagen ingaves per werkdag, elke patiënt blijft een aparte lijn.
  // Nog niet opgeslagen (pending) lijnen staan apart bovenaan de tabel, niet gegroepeerd per datum.
  const byDay = {};
  entries.forEach((e) => {
    if (!byDay[e.werkdag]) byDay[e.werkdag] = [];
    byDay[e.werkdag].push(e);
  });
  const days = Object.keys(byDay).sort();

  const periodTotal = entries.reduce((s, e) => s + parseFloat(e.bedrag), 0);
  const periodTeStorten = teStortenVoor(entries);

  const pendingTotal = pendingEntries.reduce((s, e) => s + parseFloat(e.bedrag), 0);
  const pendingTeStorten = teStortenVoor(pendingEntries);

  const selectedEntries = entries.filter((e) => selectedDays.has(e.werkdag));
  const selectedTotalPatients = selectedEntries.length;
  const selectedTotal = selectedEntries.reduce((s, e) => s + parseFloat(e.bedrag), 0);
  const selectedTeStorten = teStortenVoor(selectedEntries);

  const {
    pageItems: exportsPageItems,
    totalPages: exportsTotalPages,
    currentPage: exportsCurrentPage,
  } = paginate(exports, exportsPage);
  const {
    pageItems: paymentsPageItems,
    totalPages: paymentsTotalPages,
    currentPage: paymentsCurrentPage,
  } = paginate(payments, paymentsPage);

  const overviewEntries = filterByScope(allEntries, overviewScope);
  const perMaand = groupByPeriod(overviewEntries, monthKey);
  const perKwartaal = groupByPeriod(overviewEntries, quarterKey);

  return (
    <div className="p-4 sm:p-6">
      {errorModal.show && (
        <ErrorModal
          message={errorModal.message}
          onClose={() => setErrorModal({ show: false, message: "" })}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Ingave verwijderen"
          message={
            <>
              Wil je de ingave van <strong>€ {formatNumber(deleteTarget.bedrag)}</strong> ({deleteTarget.betaalwijze}) op{" "}
              <strong>{formatDate(deleteTarget.werkdag)}</strong> verwijderen?
            </>
          }
          confirmLabel="Verwijderen"
          danger
          busy={deleting}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {deleteExportTarget && (
        <ConfirmModal
          title="PDF-extract verwijderen"
          message={
            <>
              Wil je het extract van <strong>{formatDate(deleteExportTarget.period_from)}</strong> t.e.m.{" "}
              <strong>{formatDate(deleteExportTarget.period_to)}</strong> verwijderen? Dit verwijdert enkel het
              bewaarde PDF-bestand, niet de onderliggende ingaves.
            </>
          }
          confirmLabel="Verwijderen"
          danger
          busy={deletingExport}
          onConfirm={confirmDeleteExport}
          onCancel={() => setDeleteExportTarget(null)}
        />
      )}

      {deletePaymentTarget && (
        <ConfirmModal
          title="SEPA-betaling verwijderen"
          message={
            <>
              Wil je de betaling van <strong>{formatDate(deletePaymentTarget.period_from)}</strong> t.e.m.{" "}
              <strong>{formatDate(deletePaymentTarget.period_to)}</strong> (€{" "}
              {formatNumber(deletePaymentTarget.total_te_storten)}) verwijderen? De betrokken ingaves verliezen
              enkel de koppeling naar dit SEPA-bestand — hun status verandert niet.
            </>
          }
          confirmLabel="Verwijderen"
          danger
          busy={deletingPayment}
          onConfirm={confirmDeletePayment}
          onCancel={() => setDeletePaymentTarget(null)}
        />
      )}

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Boekje Saskia</h1>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <input
            type="date"
            value={van}
            onChange={(e) => setVan(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
          />
          <span className="text-gray-400 text-sm">t.e.m.</span>
          <input
            type="date"
            value={tot}
            onChange={(e) => setTot(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
          >
            <option value="alle">Alle</option>
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {canManage && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 mb-6">
          <h2 className="text-base font-semibold text-gray-700 mb-1">Patiënt toevoegen</h2>
          <p className="text-xs text-gray-500 mb-4">
            Eén klik op het plusje = één patiëntlijn. De velden blijven staan voor de volgende patiënt — pas aan waar nodig.
          </p>
          <form onSubmit={handleAdd} className="grid grid-cols-2 sm:grid-cols-6 gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Datum</label>
              <input
                type="date"
                value={form.werkdag}
                onChange={(e) => setForm({ ...form, werkdag: e.target.value })}
                required
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1" title="Het volledige bedrag dat de patiënt betaalde, inclusief BTW">
                Bedrag incl. BTW (€)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.bedrag}
                onChange={(e) => setForm({ ...form, bedrag: e.target.value })}
                onBlur={handleBedragBlur}
                required
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">BTW</label>
              <select
                value={form.btw_pct}
                onChange={(e) => setForm({ ...form, btw_pct: e.target.value })}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              >
                <option value="0">0%</option>
                <option value="0.06">6%</option>
                <option value="0.21">21%</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Betaalwijze</label>
              <select
                value={form.betaalwijze}
                onChange={(e) => setForm({ ...form, betaalwijze: e.target.value })}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              >
                <option value="Bancontact">Bancontact</option>
                <option value="Cash">Cash</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Notitie</label>
              <input
                type="text"
                value={form.notitie}
                onChange={(e) => setForm({ ...form, notitie: e.target.value })}
                placeholder="Optioneel..."
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <button
                type="submit"
                title="Patiënt toevoegen"
                className="px-2.5 py-1.5 border border-blue-600 bg-blue-600 text-white text-sm font-bold rounded hover:bg-blue-700 hover:border-blue-700"
              >
                +
              </button>
            </div>
          </form>
        </div>
      )}

      {pendingEntries.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-6 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
          <span className="text-sm text-blue-800">
            {pendingEntries.length} lijn{pendingEntries.length !== 1 ? "en" : ""} nog niet opgeslagen — € {formatNumber(pendingTotal)}{" "}
            (te storten: € {formatNumber(pendingTeStorten)})
          </span>
          <div className="ml-auto flex gap-2">
            <button
              onClick={() => setPendingEntries([])}
              disabled={savingBatch}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
            >
              Alles wissen
            </button>
            <button
              onClick={handleSaveBatch}
              disabled={savingBatch}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {savingBatch ? "Bezig..." : `Opslaan (${pendingEntries.length})`}
            </button>
          </div>
        </div>
      )}

      {canManage && selectedDays.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-6 bg-gray-100 border border-gray-300 rounded-lg px-4 py-3">
          <span className="text-sm text-gray-700">
            {selectedDays.size} dag{selectedDays.size !== 1 ? "en" : ""} geselecteerd — {selectedTotalPatients}{" "}
            patiënten — € {formatNumber(selectedTotal)} (te storten: € {formatNumber(selectedTeStorten)})
          </span>
          <div className="ml-auto flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedDays(new Set())}
              disabled={!!busyAction}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
            >
              Selectie wissen
            </button>
            <button
              onClick={handleExportSelected}
              disabled={!!busyAction}
              className="px-4 py-2 bg-gray-700 text-white text-sm font-medium rounded hover:bg-gray-800 disabled:opacity-50"
            >
              {busyAction === "export" ? "Bezig..." : "Genereer uittreksel"}
            </button>
            <button
              onClick={handleSepaSelected}
              disabled={!!busyAction}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {busyAction === "sepa" ? "Bezig..." : "Genereer SEPA-bestand"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
      ) : days.length === 0 && pendingEntries.length === 0 ? (
        <div className="text-gray-400 text-sm text-center py-12">
          Geen ingaves gevonden voor deze periode/filter.
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-6">
          <table className="min-w-full text-sm table-fixed">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {canManage && <th className="w-8 px-2 py-2"></th>}
                <th className="w-20 px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Datum</th>
                <th className="w-24 px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Incl. BTW</th>
                <th className="w-12 px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">BTW</th>
                <th className="w-24 px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Excl. BTW</th>
                <th className="w-24 px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Te storten</th>
                <th className="w-24 px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Betaalwijze</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Notitie</th>
                <th className="w-40 px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase">Status</th>
                {canManage && <th className="w-10 px-2 py-2"></th>}
              </tr>
            </thead>
            {pendingEntries.length > 0 && (
              <tbody className="border-b-2 border-blue-200">
                {pendingEntries.map((e) => {
                  const rowBedrag = parseFloat(e.bedrag);
                  const rowBtwPct = parseFloat(e.btw_pct);
                  const rowExclBtw = exclBtw(rowBedrag, rowBtwPct);
                  const rowTeStorten = rowExclBtw * (1 - HUUR_PCT);
                  return (
                  <tr key={e._tempId} className="bg-blue-50/60 hover:bg-blue-50">
                    {canManage && <td className="px-2 py-2"></td>}
                    <td className="px-4 py-2 text-gray-500 text-xs">{formatDate(e.werkdag)}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium text-gray-800">
                      € {formatNumber(rowBedrag)}
                    </td>
                    <td className="px-4 py-2 text-gray-600">{formatNumber(rowBtwPct * 100, 0)}%</td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-600">€ {formatNumber(rowExclBtw)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-600">€ {formatNumber(rowTeStorten)}</td>
                    <td className="px-4 py-2 text-gray-600">{e.betaalwijze}</td>
                    <td className="px-4 py-2 text-gray-400 text-xs truncate">{e.notitie || ""}</td>
                    <td className="px-2 py-2 text-center">
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Onbewaard
                      </span>
                    </td>
                    {canManage && (
                      <td className="px-2 py-2 text-right">
                        <button
                          onClick={() => removePending(e._tempId)}
                          title="Verwijder ongesaved lijn"
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          ✕
                        </button>
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            )}
            {days.map((day) => {
              const dayEntries = byDay[day];
              const dayTotal = dayEntries.reduce((s, e) => s + parseFloat(e.bedrag), 0);
              const dayTeStorten = teStortenVoor(dayEntries);
              const isExpanded = expandedDays.has(day);
              const dayStatuses = new Set(dayEntries.map((e) => e.status));
              const dayStatus = dayStatuses.size === 1 ? [...dayStatuses][0] : "";
              return (
                <tbody key={day} className="border-b border-gray-200">
                  <tr className="bg-gray-50 cursor-pointer hover:bg-gray-100" onClick={() => toggleDay(day)}>
                    {canManage && (
                      <td className="px-2 py-2" onClick={(ev) => ev.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedDays.has(day)}
                          onChange={() => toggleDaySelection(day)}
                          className="rounded border-gray-300"
                        />
                      </td>
                    )}
                    <td colSpan={7} className="px-4 py-2 text-xs font-semibold text-gray-600">
                      <span className="inline-block w-3">{isExpanded ? "▾" : "▸"}</span>{" "}
                      {formatDate(day)} — {dayEntries.length} patiënten — Ontvangen: € {formatNumber(dayTotal)} — Te
                      storten: € {formatNumber(dayTeStorten)}
                    </td>
                    <td className="px-1 py-1.5 text-center" onClick={(ev) => ev.stopPropagation()}>
                      {canManage ? (
                        <select
                          value={dayStatus}
                          onChange={(ev) => handleDayStatusChange(day, ev.target.value)}
                          title="Wijzigt de status van alle patiëntlijnen op deze dag"
                          className={`w-full rounded-full text-xs font-medium border-0 py-0.5 pl-2 pr-1 ${
                            dayStatus ? STATUS_CHIP[dayStatus] : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {!dayStatus && (
                            <option value="" disabled>
                              Gemengd
                            </option>
                          )}
                          {STATUSES.map((s) => (
                            <option key={s.value} value={s.value}>
                              {s.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                            dayStatus ? STATUS_CHIP[dayStatus] : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {dayStatus ? STATUSES.find((s) => s.value === dayStatus)?.label : "Gemengd"}
                        </span>
                      )}
                    </td>
                    {canManage && <td></td>}
                  </tr>
                  {isExpanded &&
                    dayEntries.map((e) => {
                      const isPaid = e.status === "betaald";
                      const rowBedrag = parseFloat(e.bedrag);
                      const rowBtwPct = parseFloat(e.btw_pct);
                      const rowExclBtw = exclBtw(rowBedrag, rowBtwPct);
                      const rowTeStorten = rowExclBtw * (1 - HUUR_PCT);
                      return (
                        <tr key={e.id} className="border-b border-gray-50 hover:bg-gray-50">
                          {canManage && <td className="px-2 py-2"></td>}
                          <td className="px-4 py-2 text-gray-400 text-xs">{formatDate(e.werkdag)}</td>
                          <td className="px-4 py-2 text-right tabular-nums font-medium text-gray-800">
                            € {formatNumber(rowBedrag)}
                          </td>
                          <td className="px-4 py-2 text-gray-600">{formatNumber(rowBtwPct * 100, 0)}%</td>
                          <td className="px-4 py-2 text-right tabular-nums text-gray-600">€ {formatNumber(rowExclBtw)}</td>
                          <td className="px-4 py-2 text-right tabular-nums text-gray-600">€ {formatNumber(rowTeStorten)}</td>
                          <td className="px-4 py-2 text-gray-600">{e.betaalwijze}</td>
                          <td className="px-4 py-2 text-gray-400 text-xs truncate">{e.notitie || ""}</td>
                          <td className="px-1 py-1.5 text-center">
                            <span
                              className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CHIP[e.status]}`}
                            >
                              {STATUSES.find((s) => s.value === e.status)?.label || e.status}
                            </span>
                          </td>
                          {canManage && (
                            <td className="px-2 py-2 text-right">
                              {!isPaid && (
                                <button
                                  onClick={() => setDeleteTarget(e)}
                                  title="Verwijderen"
                                  className="text-xs text-red-500 hover:text-red-700"
                                >
                                  ✕
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                </tbody>
              );
            })}
            <tfoot>
              <tr className="bg-gray-50 border-t border-gray-200 font-semibold text-gray-700">
                {canManage && <td></td>}
                <td className="px-4 py-2" colSpan={6}>
                  Totaal ({entries.length} patiënten)
                </td>
                <td className="px-4 py-2 text-xs font-normal text-gray-500">
                  € {formatNumber(periodTotal)} · Te storten: € {formatNumber(periodTeStorten)}
                </td>
                <td colSpan={canManage ? 2 : 1}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Eerder gegenereerde PDF-extracts</h3>
          {exports.length === 0 ? (
            <p className="text-xs text-gray-400">Nog geen extracts gegenereerd.</p>
          ) : (
            <>
              <ul className="space-y-2">
                {exportsPageItems.map((exp) => (
                  <li key={exp.id} className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">
                      {formatDate(exp.period_from)} – {formatDate(exp.period_to)}
                    </span>
                    <span className="flex gap-3">
                      <button
                        onClick={() => downloadExport(exp.id)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Download
                      </button>
                      {canManage && (
                        <button
                          onClick={() => setDeleteExportTarget(exp)}
                          className="text-red-500 hover:text-red-700"
                        >
                          Verwijderen
                        </button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              {renderHistoryPagination(exportsCurrentPage, exportsTotalPages, setExportsPage)}
            </>
          )}
        </div>
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Eerder gegenereerde betalingen</h3>
          {payments.length === 0 ? (
            <p className="text-xs text-gray-400">Nog geen SEPA-bestanden gegenereerd.</p>
          ) : (
            <>
              <ul className="space-y-2">
                {paymentsPageItems.map((p) => (
                  <li key={p.id} className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">
                      {formatDate(p.period_from)} – {formatDate(p.period_to)} · € {formatNumber(p.total_te_storten)}
                      <span className="text-gray-400"> ({formatDateTime(p.generated_at)})</span>
                    </span>
                    <span className="flex gap-3">
                      <button
                        onClick={() => downloadPayment(p.id)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Download
                      </button>
                      {canManage && (
                        <button
                          onClick={() => setDeletePaymentTarget(p)}
                          className="text-red-500 hover:text-red-700"
                        >
                          Verwijderen
                        </button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              {renderHistoryPagination(paymentsCurrentPage, paymentsTotalPages, setPaymentsPage)}
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-6 mb-3">
        <span className="text-sm text-gray-500 mr-1">Periode:</span>
        {OVERVIEW_SCOPES.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setOverviewScope(opt.value)}
            className={`px-3 py-1.5 text-sm rounded-full border ${
              overviewScope === opt.value
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {renderPeriodTable("Overzicht per maand", perMaand, monthLabel)}
        {renderPeriodTable("Overzicht per kwartaal", perKwartaal, quarterLabel)}
      </div>
    </div>
  );
}
